import { appendBrandNameIfPresent, PROJECT_CATCH_ERROR_LIST } from "@platformx/utilities";
import graphqlInstance from "../../config/graphqlConfig";
import {
  CREATE_CONTENT_TYPE,
  DELETE_CONTENT_TYPE,
  FETCH_FORM_DATA,
  PUBLISH_CONTENT_TYPE,
  UPDATE_CONTENT_TYPE,
} from "../../graphQL/mutations/contentTypeMutations";
import {
  FETCH_CONTENT_BY_PATH,
  FETCH_CONTENT_TYPE_LIST_ALL,
} from "../../graphQL/queries/contentTypesQueries";
import { SearchContentListQueries } from "../../graphQL/queries/searchQueries";
import { ROW_SIZE } from "../../utils/constants";
import { sortedData } from "../../utils/helper";
import { ApiResponse } from "../../utils/types";
import { mapFetchALL } from "./mapper";

const withBrandNameInput = (variables: any, isShared = false): any => {
  if (!variables?.input) return variables;
  return { ...variables, input: appendBrandNameIfPresent(variables.input, isShared) };
};

// FetchQueries
export const fetchContentByPath = FETCH_CONTENT_BY_PATH;
export const fetchContentTypeList = FETCH_CONTENT_TYPE_LIST_ALL;

// MutateQueries
export const createContentType = CREATE_CONTENT_TYPE;
export const updateContentType = UPDATE_CONTENT_TYPE;
export const publishContentType = PUBLISH_CONTENT_TYPE;
export const deleteContentType = DELETE_CONTENT_TYPE;

const contentTypeAPIs = {
  createContentType: CREATE_CONTENT_TYPE,
  updateContentType: UPDATE_CONTENT_TYPE,
  publishContentType: PUBLISH_CONTENT_TYPE,
  deleteContentType: DELETE_CONTENT_TYPE,
  fetchContentByPath: FETCH_CONTENT_BY_PATH,
  fetchContentTypeListAll: FETCH_CONTENT_TYPE_LIST_ALL,
  fetchContent: async (input: any) => {
    const { isShared = false, ...variables } = input;
    try {
      const { data } = await graphqlInstance.query({
        query: FETCH_CONTENT_BY_PATH,
        variables,
        context: {
          skipBrandNameHeader: isShared,
        },
        fetchPolicy: "no-cache",
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:fetchContent`, err);
      return {};
    }
  },
  fetchContentAll: async (input: any) => {
    const { isShared = false, ...variables } = input;
    try {
      const { data } = await graphqlInstance.query({
        query: SearchContentListQueries.FETCH_CONTENT_TYPE_LIST,
        variables: withBrandNameInput(variables, isShared),
        context: {
          skipBrandNameHeader: isShared,
        },
        fetchPolicy: "no-cache",
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:fetchContentAll`, err);
      return {};
    }
  },

  //used in create page
  fetchSearchContent: async (
    contentType: string,
    location: { state: any },
    filter: string,
    startIndex: number,
    _: any,
    // eslint-disable-next-line default-param-last
    reloadContent = false,
    options?: string | { schemaType?: string; isShared?: boolean },
  ) => {
    const schemaType = typeof options === "string" ? options : options?.schemaType;
    const isShared = typeof options === "string" ? false : options?.isShared === true;
    const newPagination = {
      start: reloadContent ? 0 : startIndex,
      rows: ROW_SIZE,
    };
    const variables = mapFetchALL(location.state, filter, contentType, newPagination, schemaType);
    const { data } = await graphqlInstance.query({
      query: SearchContentListQueries.FETCH_CONTENT_TYPE_LIST,
      variables,
      context: {
        skipBrandNameHeader: isShared,
      },
      fetchPolicy: "no-cache",
    });
    const sortedContent = sortedData(data?.authoring_getContentTypeItems || []);
    return sortedContent;
  },

  fetchContentTypeList: async (input: any) => {
    const { isShared = false, ...variables } = input;
    try {
      const { data, loading } = await graphqlInstance.query({
        query: SearchContentListQueries.FETCH_CONTENT_TYPE_LIST,
        variables: withBrandNameInput(variables, isShared),
        context: {
          skipBrandNameHeader: isShared,
        },
        fetchPolicy: "no-cache",
      });
      return { data: data, loading: loading };
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:fetchContentTypeList`, err);
      return {};
    }
  },
  fetchSuggestions: async (input: any) => {
    const { isShared = false, ...variables } = input;
    try {
      const { data, loading } = await graphqlInstance.query({
        query: SearchContentListQueries.FETCH_CONTENT_TYPE_LIST,
        variables: withBrandNameInput(variables, isShared),
        context: {
          skipBrandNameHeader: isShared,
        },
        fetchPolicy: "no-cache",
      });
      return { data: data, loading: loading };
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:fetchSuggestions`, err);
      return {};
    }
  },

  fetchCourseContent: async (
    contentType: any,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    location: any,
    _: any,
    startIndex: any,
    contentList: any,
    reloadContent = false,
  ) => {
    const pagination = {
      start: reloadContent ? 0 : startIndex,
      rows: ROW_SIZE,
    };
    const variables = {
      pagination,
      filter: contentType,
      isListing: true,
    };
    const { data } = await graphqlInstance.query({
      query: SearchContentListQueries.FETCH_COURSE_LIST,
      variables: variables,
      fetchPolicy: "no-cache",
    });
    const sortedContent = sortedData(data?.authoring_recentContents || []);

    return {
      type: "UPDATE_CONTENT",
      content: reloadContent
        ? [...JSON.parse(JSON.stringify(sortedContent))]
        : [...contentList, ...JSON.parse(JSON.stringify(sortedContent))],
      loading: false,
      newDataSize: [...JSON.parse(JSON.stringify(sortedContent))].length,
      contentType: contentType,
    };
  },
  fetchFormData: async (input: any) => {
    const { isShared = false, ...variables } = input;
    try {
      const { data } = await graphqlInstance.query({
        query: FETCH_FORM_DATA,
        variables: withBrandNameInput(variables, isShared),
        context: {
          skipBrandNameHeader: isShared,
        },
        fetchPolicy: "no-cache",
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:fetchFormData`, err);
      return {};
    }
  },
};

export default contentTypeAPIs;

export const eventAPIS = {
  fetchContent: async (input: any) => {
    const { isShared = false, ...variables } = input;
    try {
      const { data } = await graphqlInstance.query({
        query: FETCH_CONTENT_BY_PATH,
        variables: withBrandNameInput(variables, isShared),
        context: {
          skipBrandNameHeader: isShared,
        },
        fetchPolicy: "no-cache",
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:fetchContent`, err);
      return {};
    }
  },
  createContentType: async <T>(input: any): Promise<ApiResponse<T>> => {
    const { isShared = false, ...variables } = input;
    try {
      const { data } = await graphqlInstance.query({
        query: CREATE_CONTENT_TYPE,
        variables: withBrandNameInput(variables, isShared),
        context: {
          skipBrandNameHeader: isShared,
        },
        fetchPolicy: "no-cache",
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:createContentType`, err);
      throw err;
    }
  },
  updateContentType: async <T>(input: any): Promise<ApiResponse<T>> => {
    const { isShared = false, ...variables } = input;
    try {
      const { data } = await graphqlInstance.query({
        query: UPDATE_CONTENT_TYPE,
        variables: withBrandNameInput(variables, isShared),
        context: {
          skipBrandNameHeader: isShared,
        },
        fetchPolicy: "no-cache",
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:updateContentType`, err);
      throw err;
    }
  },
  publishContentType: async <T>(input: any): Promise<ApiResponse<T>> => {
    const { isShared = false, ...variables } = input;
    try {
      const { data } = await graphqlInstance.query({
        query: PUBLISH_CONTENT_TYPE,
        variables: withBrandNameInput(variables, isShared),
        context: {
          skipBrandNameHeader: isShared,
        },
        fetchPolicy: "no-cache",
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:publishContentType`, err);
      throw err;
    }
  },
};
export const AIAPIS = {
  fetchAIContent: async <T>(input: T, GqlQuery): Promise<any> => {
    try {
      const { data } = await graphqlInstance.query({
        query: GqlQuery,
        variables: input,
        fetchPolicy: "network-only",
      });
      return data;
    } catch (err: any) {
      console.error(`fetchSelectedCollectionItemDetail:fetchSelectedCollectionItemDetail`, err);
      throw err;
    }
  },
};
